import React, { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { Form, Container, Button, Toast, Spinner } from "react-bootstrap";
import { physicianApi } from "../../../CONSTANTS";
import styles from "./PhysicianContactForm.module.css";
import { axiosPost } from "../../../UTILS/AXIOS";
import FormSecurity from "../../FORM_SECURITY";
import {
  rules,
  securityFields,
  isSuccess,
  failureMessage,
  applyServerErrors,
} from "../../../UTILS/VALIDATION";

const schema = Yup.object().shape({
  fullName: rules.name("Full Name"),
  specialization: rules.text("Specialization", 150),
  hospital: rules.text("Hospital/Clinic/Institution Name", 200),
  email: rules.email(),
  phone: rules.phone(),
  address: rules.text("Address", 200),
  city: rules.text("City", 100),
  state: rules.text("State", 100),
  message: rules.text("Message", 2000, false),
  ...securityFields,
});

const formFields = [
  { name: "fullName", label: "Full Name", type: "text", required: true, autoComplete: "name" },
  { name: "specialization", label: "Specialization / Department", type: "text", required: true },
  { name: "hospital", label: "Hospital / Clinic / Institution Name", type: "text", required: true, autoComplete: "organization" },
  { name: "email", label: "Email Address", type: "email", required: true, autoComplete: "email" },
  { name: "phone", label: "Phone Number", type: "tel", required: true, autoComplete: "tel" },
  { name: "address", label: "Address", type: "text", required: true, autoComplete: "street-address" },
  { name: "city", label: "City", type: "text", required: true, autoComplete: "address-level2" },
  { name: "state", label: "State", type: "text", required: true, autoComplete: "address-level1" },
  { name: "message", label: "Interest Area / Message / Additional Notes", type: "textarea", required: false, maxLength: 2000 },
];

const PhysicianContactForm = () => {
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastVariant, setToastVariant] = useState("success");
  const [loading, setLoading] = useState(false);
  const captcha = useRef(null);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    setError,
    formState: { errors },
  } = useForm({ resolver: yupResolver(schema), mode: "onTouched" });

  const onSubmit = async (data) => {
    setLoading(true);
    const result = await axiosPost(physicianApi, { ...data, formType: "physicianContact" });
    if (isSuccess(result)) {
      setToastMessage("Thank you. Our team will be in touch soon.");
      setToastVariant("success");
      reset();
    } else {
      applyServerErrors(result, setError, Object.keys(schema.fields));
      setToastMessage(failureMessage(result));
      setToastVariant("danger");
    }
    captcha.current?.reset();
    setShowToast(true);
    setLoading(false);
  };

  const renderField = ({ name, label, type, required, autoComplete, maxLength }) => (
    <Form.Group controlId={`form${name}`} key={name}>
      <Form.Label className={styles.label}>
        {label}
        {required && <span className={styles.contactFormTextDanger}> *</span>}
      </Form.Label>
      <Form.Control
        {...(type === "textarea" ? { as: "textarea", rows: 3 } : { type })}
        {...register(name)}
        autoComplete={autoComplete}
        maxLength={maxLength}
        aria-required={required}
        isInvalid={!!errors[name]}
        disabled={loading}
        className={styles.contactFormFormControl}
      />
      <Form.Control.Feedback type="invalid">{errors[name]?.message}</Form.Control.Feedback>
    </Form.Group>
  );

  return (
    <Container className={`${styles.contactForm} p-5 position-relative`}>
      {loading && (
        <div className={styles.overlay}>
          <Spinner animation="border" variant="light" />
        </div>
      )}

      <Form noValidate onSubmit={handleSubmit(onSubmit)} className={styles.contactFormFormSection}>
        {formFields.map(renderField)}

        <FormSecurity ref={captcha} register={register} setValue={setValue} error={errors.recaptchaToken} theme="dark" />

        <Form.Group>
          <div className="d-flex justify-content-center mt-4">
            <Button type="submit" disabled={loading} className={styles.contactFormFormSubmit}>
              Submit
            </Button>
          </div>
        </Form.Group>
      </Form>

      <Toast
        onClose={() => setShowToast(false)}
        show={showToast}
        delay={6000}
        autohide
        role="status"
        className={`bg-${toastVariant} text-white position-fixed`}
        style={{ bottom: "10px", right: "10px", zIndex: 10000 }}
      >
        <Toast.Body>{toastMessage}</Toast.Body>
      </Toast>
    </Container>
  );
};

export default PhysicianContactForm;
